import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Breadcrumbs
} from '@mui/material';
import {
  NavigateBefore as BackIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  FileDownload as ExcelIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { supplierService } from '../../services/supplierService';
import { enqueueSnackbar } from 'notistack';
import { formatCurrency } from '../../utils/currencyFormatter';
import { formatDate } from '../../utils/dateFormatter';
import supplierCreditService from '../../services/supplierCreditService';
import { format, parseISO, isValid } from 'date-fns';

const SupplierLedger = () => {
  const { supplierId } = useParams();
  const navigate = useNavigate();

  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transactionsLoading, setTransactionsLoading] = useState(true);

  const [transactions, setTransactions] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [accountSummary, setAccountSummary] = useState({
    openingBalance: 0,
    totalPurchases: 0,
    totalPaid: 0,
    totalReturns: 0,
    balanceDue: 0
  });

  const fetchSupplierDetails = async () => {
    try {
      setLoading(true);
      setTransactionsLoading(true);

      const supplierResponse = await supplierService.getSupplierById(supplierId);
      setSupplier(supplierResponse);

      const [creditSummary, creditTransactions] = await Promise.all([
        supplierCreditService.getSupplierCreditSummary(supplierId),
        supplierCreditService.getSupplierTransactions(supplierId)
      ]);

      setAccountSummary({
        openingBalance: creditSummary.openingBalance || 0,
        totalPurchases: creditSummary.totalCreditPurchases || 0,
        totalPaid: creditSummary.totalPayments || 0,
        totalReturns: creditSummary.totalReturns || 0,
        balanceDue: creditSummary.balanceDue || 0
      });

      if (creditTransactions && creditTransactions.length > 0) {
        const formattedTransactions = creditTransactions.map(tx => {
          const transactionType = tx.transactionType || tx.transaction_type || '';
          const isDebit = ['PAYMENT', 'RETURN', 'CREDIT_REFUND', 'CREDIT_ADJUSTMENT_DECREASE'].includes(transactionType);

          const transactionDate = tx.transactionDate || tx.transaction_date || null;
          const displayDate = formatDateDisplay(transactionDate);

          return {
            id: tx.id || 0,
            rawDate: transactionDate,
            displayDate,
            type: transactionType,
            reference: tx.referenceId || tx.reference_id || `TRANS-${tx.id || 0}`,
            description: getTransactionDescription(tx),
            debit: isDebit ? (parseFloat(tx.amount || 0) || 0) : 0,
            credit: !isDebit ? (parseFloat(tx.amount || 0) || 0) : 0,
            balance: parseFloat(tx.runningBalance || tx.running_balance || 0),
            status: getTransactionStatus(transactionType),
            paymentMethod: tx.paymentMethod || tx.payment_method || ''
          };
        });

        formattedTransactions.sort((a, b) => {
          const aDate = parseDate(a.rawDate) || new Date(0);
          const bDate = parseDate(b.rawDate) || new Date(0);
          return aDate - bDate;
        });

        setTransactions(formattedTransactions);
      } else {
        const placeholderTransaction = {
          id: 1,
          displayDate: formatDateDisplay(new Date()),
          type: 'OPENING_BALANCE',
          reference: `SUPPLIER-${supplierId}`,
          description: 'Opening Payable Balance',
          debit: 0,
          credit: parseFloat(supplierResponse.payableBalance || 0),
          balance: parseFloat(supplierResponse.payableBalance || 0),
          status: 'PENDING'
        };
        setTransactions([placeholderTransaction]);
      }

      setLoading(false);
      setTransactionsLoading(false);
    } catch (error) {
      enqueueSnackbar('Failed to load supplier details', { variant: 'error' });
      setLoading(false);
      setTransactionsLoading(false);
    }
  };

  const getTransactionDescription = (transaction) => {
    const type = transaction.transactionType || transaction.transaction_type;
    const method = transaction.paymentMethod || transaction.payment_method || '';
    const refId = transaction.referenceId || transaction.reference_id || '';
    const notes = transaction.notes || '';

    switch (type) {
      case 'OPENING_BALANCE':
        return 'Opening Payable Balance';
      case 'CREDIT_PURCHASE':
        return refId ? `Credit Purchase - PO #${refId}` : 'Credit Purchase';
      case 'PAYMENT':
        return `Payment To Supplier - ${method}${notes ? ` - ${notes}` : ''}`;
      case 'RETURN':
        return refId ? `Purchase Return - ${refId}` : 'Purchase Return';
      default:
        return notes || 'Transaction';
    }
  };

  const getTransactionStatus = (transactionType) => {
    switch (transactionType) {
      case 'PAYMENT':
      case 'RETURN':
        return 'PAID';
      case 'OPENING_BALANCE':
      case 'CREDIT_PURCHASE':
        return 'PENDING';
      default:
        return 'PENDING';
    }
  };

  const parseDate = (val) => {
    if (!val) return null;
    
    // Handle array format [yyyy, mm, dd, hh, mm, ss]
    if (Array.isArray(val)) {
      const [y, m, d, hh = 0, mm = 0, ss = 0] = val;
      const date = new Date(y, m - 1, d, hh, mm, ss);
      return isValid(date) ? date : null;
    }

    if (typeof val === 'number') {
      const d = new Date(val);
      return isValid(d) ? d : null;
    }

    if (typeof val === 'string') {
      // Try parseISO first
      const d = parseISO(val);
      if (isValid(d)) return d;
      
      // Fallback for other string formats
      const d2 = new Date(val);
      if (isValid(d2)) return d2;
      
      return null;
    }

    if (typeof val === 'object') {
      const y = val.year || val.y || null;
      const m = (val.month || val.m || 1) - 1;
      const dday = val.day || val.d || 1;
      const hh = val.hour || val.h || 0;
      const mm = val.minute || val.min || 0;
      const ss = val.second || val.s || 0;
      if (y) {
        const d = new Date(y, m, dday, hh, mm, ss);
        return isValid(d) ? d : null;
      }
    }
    return null;
  };

  const formatDateDisplay = (val) => {
    const d = parseDate(val);
    if (!d) return 'N/A';
    return format(d, 'dd/MM/yyyy');
  };

  useEffect(() => {
    if (supplierId) {
      fetchSupplierDetails();
    }
  }, [supplierId]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportToExcel = async () => {
    try {
      const supplierSummaryData = [
        ['SUPPLIER LEDGER REPORT'],
        ['Generated on:', new Date().toLocaleDateString()],
        [''],
        ['SUPPLIER INFORMATION'],
        ['Supplier Name', supplier?.name || 'N/A'],
        ['Phone', supplier?.phone || 'N/A'],
        ['Email', supplier?.email || 'N/A'],
        ['Address', supplier?.address || 'N/A'],
        [''],
        ['ACCOUNT SUMMARY'],
        ['Opening Payable', formatCurrency(accountSummary.openingBalance)],
        ['Total Credit Purchases', formatCurrency(accountSummary.totalPurchases)],
        ['Total Payments', formatCurrency(accountSummary.totalPaid)],
        ['Total Returns', formatCurrency(accountSummary.totalReturns)],
        ['Current Balance Due', formatCurrency(accountSummary.balanceDue)],
        [''],
        ['Ledger Transactions:']
      ];

      const transactionsData = transactions.map((transaction, index) => ({
        'S.No': index + 1,
        'Date': transaction.displayDate,
        'Description': transaction.description,
        'Type': transaction.type,
        'Reference': transaction.reference || 'N/A',
        'Debit': transaction.debit.toFixed(2),
        'Credit': transaction.credit.toFixed(2),
        'Balance': transaction.balance >= 0
          ? `${transaction.balance.toFixed(2)} (cr)`
          : `${Math.abs(transaction.balance).toFixed(2)} (dr)`,
        'Status': transaction.status
      }));

      const transactionHeaders = [
        'S.No', 'Date', 'Description', 'Type', 'Reference', 'Debit', 'Credit', 'Balance', 'Status'
      ];
      const transactionRows = transactionsData.map(tx =>
        transactionHeaders.map(header => tx[header])
      );

      const finalSheetData = [
        ...supplierSummaryData,
        transactionHeaders,
        ...transactionRows
      ];

      const ws = XLSX.utils.aoa_to_sheet(finalSheetData);
      ws['!cols'] = [
        { wch: 8 },
        { wch: 12 },
        { wch: 30 },
        { wch: 15 },
        { wch: 20 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 }
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Supplier Ledger');

      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const fileData = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(fileData);
      const link = document.createElement('a');
      link.href = url;
      link.download = `supplier_ledger_${supplier?.name || 'unknown'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      enqueueSnackbar('Supplier ledger exported to Excel successfully!', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to export supplier ledger to Excel', { variant: 'error' });
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link
              to="/reports/credit-suppliers"
              style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center' }}
            >
              <BackIcon fontSize="small" sx={{ mr: 0.5 }} />
              Supplier Credit
            </Link>
            <Typography color="text.primary">Supplier Ledger</Typography>
          </Breadcrumbs>

          <Button
            variant="outlined"
            startIcon={<ExcelIcon />}
            onClick={handleExportToExcel}
          >
            Export to Excel
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : supplier ? (
          <>
            <Paper sx={{ mb: 4, p: 3, borderRadius: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <BusinessIcon fontSize="large" color="primary" sx={{ mr: 2 }} />
                    <Typography variant="h4" component="h1">
                      {supplier.name}
                    </Typography>
                    <Chip
                      label="Supplier"
                      size="small"
                      color="primary"
                      sx={{ ml: 2 }}
                    />
                  </Box>

                  <Box sx={{ ml: 5 }}>
                    {supplier.phone && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body1">{supplier.phone}</Typography>
                      </Box>
                    )}

                    {supplier.email && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <EmailIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body1">{supplier.email}</Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                    <LocationIcon fontSize="small" sx={{ mr: 1, mt: 0.5, color: 'text.secondary' }} />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        Address
                      </Typography>
                      <Typography variant="body1" component="div">
                        {supplier.address && (
                          <Box component="span" sx={{ display: 'block' }}>
                            {supplier.address}
                          </Box>
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            <Paper sx={{ mb: 4, overflow: 'hidden', borderRadius: 2 }}>
              <Box sx={{ bgcolor: '#1976d2', color: 'white', p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">
                  Account Summary
                </Typography>
              </Box>

              <Grid container>
                <Grid item xs={12} sm={6} md={2.4}>
                  <Box sx={{ p: 2, borderRight: '1px solid #eee', height: '100%' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Opening Payable
                    </Typography>
                    <Typography variant="h6">
                      {formatCurrency(accountSummary.openingBalance)}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={2.4}>
                  <Box sx={{ p: 2, borderRight: '1px solid #eee', height: '100%' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Total credit purchases
                    </Typography>
                    <Typography variant="h6">
                      {formatCurrency(accountSummary.totalPurchases)}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={2.4}>
                  <Box sx={{ p: 2, borderRight: '1px solid #eee', height: '100%' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Total paid
                    </Typography>
                    <Typography variant="h6">
                      {formatCurrency(accountSummary.totalPaid)}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={2.4}>
                  <Box sx={{ p: 2, borderRight: '1px solid #eee', height: '100%' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Total returns
                    </Typography>
                    <Typography variant="h6" color="success.main">
                      {formatCurrency(accountSummary.totalReturns)}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6} md={2.4}>
                  <Box sx={{ p: 2, bgcolor: '#fafafa', height: '100%' }}>
                    <Typography variant="subtitle2" color="error" gutterBottom>
                      Balance due
                    </Typography>
                    <Typography variant="h6" color="error">
                      {formatCurrency(accountSummary.balanceDue)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            <Paper sx={{ mb: 4, borderRadius: 2 }}>
              <Box sx={{ bgcolor: '#f5f5f5', p: 2 }}>
                <Typography variant="h6">
                  Ledger Transactions
                </Typography>
              </Box>

              {transactionsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Reference</TableCell>
                          <TableCell>Description</TableCell>
                          <TableCell align="right">Debit</TableCell>
                          <TableCell align="right">Credit</TableCell>
                          <TableCell align="right">Balance</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {transactions.length > 0 ? (
                          transactions
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((transaction) => (
                              <TableRow key={transaction.id}>
                                <TableCell>{transaction.displayDate}</TableCell>
                                <TableCell>{transaction.type}</TableCell>
                                <TableCell>{transaction.reference}</TableCell>
                                <TableCell>{transaction.description}</TableCell>
                                <TableCell align="right">
                                  {transaction.debit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </TableCell>
                                <TableCell align="right">
                                  {transaction.credit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </TableCell>
                                <TableCell align="right">
                                  {transaction.balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={transaction.status}
                                    size="small"
                                    color={transaction.status === 'PAID' ? 'success' : 'warning'}
                                  />
                                </TableCell>
                              </TableRow>
                            ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={8} align="center">
                              No transactions found
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={transactions.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                  />
                </>
              )}
            </Paper>
          </>
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" color="error">
              Supplier not found
            </Typography>
            <Button
              variant="contained"
              color="primary"
              sx={{ mt: 2 }}
              onClick={() => navigate('/reports/credit-suppliers')}
            >
              Back to Supplier Credit
            </Button>
          </Paper>
        )}
      </Box>
    </Layout>
  );
};

export default SupplierLedger;
